import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';

import { BaseDirective } from '../base.component';

@Component({
  selector: 'app-alphabet-filter',
  imports: [CommonModule],
  standalone: true,
  template: `
    <div class="alphabet-filter-container">
      <ul class="d-flex p-0 mb-0">
        <li
          (click)="filterByLetter('')"
          (keyup.enter)="filterByLetter('')"
          tabindex="0"
          data-qa="alphabet-filter-list-item"
        >
          <button
            type="button"
            [ngClass]="{ 'letter-active': !activeLetter }"
            class="letter"
            data-qa="alphabet-filter-all-button"
          >
            ALL
          </button>
        </li>
        @for (letter of alphabet; track letter) {
          <li>
            <button
              type="button"
              class="letter"
              [ngClass]="{ 'letter-active': activeLetter === letter.char }"
              (click)="filterByLetter(letter.char)"
              [disabled]="letter.disabled"
              data-qa="alphabet-filter-letter-button"
            >
              {{ letter.char }}
            </button>
          </li>
        }
      </ul>
    </div>
  `,
  styleUrls: ['./alphabet-filter.component.scss'],
})
export class AlphabetFilterComponent<T extends Record<string, unknown>>
  extends BaseDirective
  implements OnInit
{
  _filterProperty$ = new BehaviorSubject<string>('');
  @Input() set filterProperty(val: string) {
    this._filterProperty$.next(val);
  }

  get filterProperty() {
    return this._filterProperty$.value;
  }

  _itemList$ = new BehaviorSubject<T[]>([]);
  @Input() set itemList(val: T[]) {
    this._itemList$.next(val);
  }

  get itemList() {
    return this._itemList$.value;
  }

  @Input() activeLetter: string;
  @Output() filter = new EventEmitter<T[]>();

  alphabet: { char: string; disabled: boolean }[];

  constructor() {
    super();
    const alphabetStr = 'abcdefghijklmnopqrstuvwxyz';
    this.alphabet = alphabetStr
      .toUpperCase()
      .split('')
      .map((x) => ({ char: x, disabled: true }));
  }

  ngOnInit(): void {
    if (!this.itemList) {
      console.error('Item list required');
    }
    if (!this.filterProperty) {
      console.error('Filter property required');
    }

    this._subscriptions.push(
      combineLatest([this._itemList$, this._filterProperty$]).subscribe(
        ([items, filterProperty]) => {
          const validLetters = new Set();
          items.forEach((i) => {
            const value = i[filterProperty] as string;
            if (value) {
              validLetters.add(value.charAt(0).toUpperCase());
            }
          });
          this.alphabet.forEach((l) => {
            if (validLetters.has(l.char)) {
              l.disabled = false;
            } else {
              l.disabled = true;
            }
          });
        },
      ),
    );
  }

  filterByLetter(letter: string) {
    this.activeLetter = letter;

    if (letter.length) {
      const filteredList = this.itemList.filter((i) => {
        const value = i[this.filterProperty] as string;
        return value && value.toUpperCase().startsWith(letter.toUpperCase());
      });
      this.filter.emit(filteredList);
    } else {
      // All items selected
      this.filter.emit(this.itemList);
    }
  }
}
